/**
 * Blog listing behaviour. Two elements, one file, one shared rule: every link
 * here is real, and every failure hands control back to the href rather than
 * leaving a dead control behind.
 *
 *   <blog-filter>    — tab clicks swap the listing in place instead of navigating
 *   <blog-load-more> — "Load more" fetches the next page and appends it
 *
 * Both talk to the SAME server-rendered pages. The tabs point at Shopify's tag
 * routes (/blogs/x/tagged/y), so the filtering is done by the server and
 * `paginate` pages the filtered list — which is what lets "Load more" keep
 * working inside a category. The JS only removes the page reloads; it never
 * decides what is in the list.
 */

/**
 * <blog-filter> — turns the tag tabs into an in-place swap.
 *
 * Fetches the tab's own URL through the Section Rendering API (?section_id=),
 * which returns just this section rather than a whole page of header, footer and
 * scripts, then replaces this element's contents with the matching region of the
 * response. Because the markup that comes back is the same markup the server
 * would have sent on a full navigation, the tab states, the grid and the
 * "Load more" button all arrive already correct — there is no client-side state
 * to keep in sync, and <blog-load-more> re-initialises itself when the new
 * markup is inserted.
 */
if (!customElements.get("blog-filter")) {
  customElements.define(
    "blog-filter",
    class BlogFilter extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.handleClick = this.handleClick.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
      }

      connectedCallback() {
        // Delegated, because the tabs themselves are replaced on every swap.
        this.addEventListener("click", this.handleClick);
        window.addEventListener("popstate", this.handlePopState);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        window.removeEventListener("popstate", this.handlePopState);
      }

      handleClick(event) {
        // Let the browser have modified clicks: open-in-new-tab must keep working.
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const tab = event.target.closest(".blog__tab");
        if (!tab || !this.contains(tab)) return;

        const url = tab.getAttribute("href");
        if (!url) return;

        // Re-clicking the open category would swap it for an identical copy.
        if (tab.classList.contains("is-active")) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        this.navigate(url, true);
      }

      // Back / forward must move between categories. A popstate can also land
      // somewhere this element cannot render — an article, or whatever page the
      // reader arrived from — and swapping the listing in that case would leave
      // the blog grid sitting under someone else's URL. Only handle URLs that
      // belong to this blog and let the browser do its own thing otherwise.
      handlePopState() {
        const path = window.location.pathname;
        const base = this.dataset.blogUrl;
        if (!base || (path !== base && path.indexOf(`${base}/tagged/`) !== 0)) return;
        this.navigate(path + window.location.search, false);
      }

      async navigate(url, push) {
        if (this.loading) return;
        this.loading = true;
        this.setAttribute("aria-busy", "true");

        try {
          const sectionId = this.dataset.sectionId;
          const separator = url.indexOf("?") === -1 ? "?" : "&";
          const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(sectionId)}`);
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), "text/html");
          const next = doc.querySelector("[data-blog-content]");
          if (!next) throw new Error("No blog content in the response");

          this.innerHTML = next.innerHTML;

          if (push) history.pushState({ blogFilter: true }, "", url);

          this.dispatchEvent(
            new CustomEvent("blog:filtered", { bubbles: true, detail: { url } }),
          );
        } catch (error) {
         
          if (push) {
            window.location.href = url;
          } else {
            window.location.reload();
          }
        } finally {
          this.loading = false;
          this.removeAttribute("aria-busy");
        }
      }
    },
  );
}


if (!customElements.get("blog-load-more")) {
  customElements.define(
    "blog-load-more",
    class BlogLoadMore extends HTMLElement {
      constructor() {
        super();
        this.grid = this.querySelector("[data-blog-grid]");
        this.button = this.querySelector("[data-blog-load-more]");
        this.loading = false;
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.button?.addEventListener("click", this.handleClick);
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.handleClick);
      }

      async handleClick(event) {
        // Nothing to append into, or nothing to append — let the link navigate.
        if (!this.grid || !this.button) return;

        const url = this.button.getAttribute("href");
        if (!url) return;

        event.preventDefault();
        if (this.loading) return;

        this.loading = true;
        this.button.setAttribute("aria-busy", "true");

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);

          const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html");
          const cards = nextDocument.querySelectorAll("[data-blog-grid] > *");
          if (cards.length === 0) throw new Error("No cards found in the next page");

          const appended = Array.from(cards).map((card) => this.grid.appendChild(document.importNode(card, true)));

          // Re-point the button at page N+1, or retire it on the last page.
          const nextUrl = nextDocument.querySelector("[data-blog-load-more]")?.getAttribute("href");
          if (nextUrl) {
            this.button.setAttribute("href", nextUrl);
          } else {
            this.button.closest("[data-blog-load-more-wrap]")?.remove();
          }

          // Send focus to the first new card so keyboard and screen reader users
          // land on the content they just asked for instead of losing their place.
          appended[0]?.querySelector("a")?.focus();

          this.dispatchEvent(
            new CustomEvent("blog:loaded", {
              bubbles: true,
              detail: { added: appended.length, nextUrl: nextUrl || null },
            }),
          );
        } catch (error) {
          // Give up on enhancing and do what the link says.
          window.location.href = url;
        } finally {
          this.loading = false;
          this.button?.removeAttribute("aria-busy");
        }
      }
    },
  );
}
